import { StandardEvents } from '@shopify/events';

/*
 * Keeps the Dimensions row in step with the variant picker.
 *
 * The block is server-rendered and stays that way: variant-picker morphs only
 * itself on a normal product page, so nothing here is replaced when the
 * customer changes size. Rather than re-fetch the section for one line of
 * text, every variant's dimensions are already in the page as JSON and this
 * swaps the string.
 */
class PdpSpecsDimensions extends HTMLElement {
  connectedCallback() {
    this.value = this.querySelector('[data-spec-dimensions-value]');
    const source = this.querySelector('[data-spec-dimensions-map]');
    if (!this.value || !source) return;

    try {
      this.map = JSON.parse(source.textContent);
    } catch (error) {
      // A malformed map should leave the server-rendered value alone rather
      // than blanking a row the page already rendered correctly.
      console.warn('[pdp-specs] dimensions map is not valid JSON:', error);
      return;
    }

    this.target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!this.target) return;
    this.target.addEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  disconnectedCallback() {
    this.target?.removeEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  /* The id arrives in more than one shape depending on where the event came
     from -- a bare number from the picker, a gid://shopify/ProductVariant/123
     from elsewhere. Both end in the digits the map is keyed by, so the tail is
     what gets used rather than trusting either form. A mismatch here fails
     silently: the lookup misses and the row simply never changes, which looks
     exactly like the listener not running at all. */
  static variantId(value) {
    const match = String(value ?? '').match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  handleSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const id = PdpSpecsDimensions.variantId(detail?.resource?.id ?? detail?.variantId);
        if (!id) return;

        // A variant written as pieces carries its rows as markup; swap them
        // in whole rather than flattening them to one line of text.
        const template = this.querySelector(`template[data-spec-dimensions-variant="${id}"]`);
        if (template instanceof HTMLTemplateElement) {
          this.value.replaceChildren(template.content.cloneNode(true));
          return;
        }

        const next = this.map[id];
        // A variant missing from the map keeps what is on screen. Silence is
        // the wrong answer here: the row was right a moment ago.
        if (next) this.value.textContent = next;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-specs] event promise rejected:', error);
      });
  };
}

if (!customElements.get('pdp-specs-dimensions')) {
  customElements.define('pdp-specs-dimensions', PdpSpecsDimensions);
}
